/**
 * @remarks Runtime 导航地址白名单
 * @module @yimoka/designable-playground/runtime
 */

const RUNTIME_NAVIGATION_SCHEME_PATTERN = /^([a-z][a-z\d+.-]*):/i;
const RUNTIME_NAVIGATION_ALLOWED_SCHEMES = new Set([
  'http',
  'https',
  'mailto',
  'tel',
]);

/** 识别会被浏览器 URL 解析器忽略或折叠的 ASCII 控制字符。 */
const hasRuntimeNavigationControlCharacter = (value: string): boolean => (
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  })
);

/** 把字符串或 Router 风格对象归一为不依赖宿主 Router 的导航地址。 */
export const resolveRuntimeNavigationHref = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const record = value as Record<string, unknown>;
  return ['pathname', 'search', 'hash']
    .map(key => (typeof record[key] === 'string' ? record[key] : ''))
    .join('');
};

/** 仅允许相对地址及明确批准的网络、邮件和电话协议。 */
export const isSafeRuntimeNavigationHref = (href: string): boolean => {
  if (href.trim() !== href || hasRuntimeNavigationControlCharacter(href)) return false;
  // 协议相对地址和反斜杠地址会绕过显式协议白名单，Runtime v1 统一拒绝。
  if (href.startsWith('//') || href.includes('\\')) return false;
  const scheme = href.match(RUNTIME_NAVIGATION_SCHEME_PATTERN)?.[1]?.toLowerCase();
  return !scheme || RUNTIME_NAVIGATION_ALLOWED_SCHEMES.has(scheme);
};

/** Link ping 以空白分隔多个地址，必须逐项通过导航白名单才能交给浏览器。 */
export const isSafeRuntimeNavigationPing = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  if (!value) return true;
  if (value.trim() !== value) return false;
  return value.split(/\s+/u).every(isSafeRuntimeNavigationHref);
};

/** 在渲染边界把危险或畸形导航降级为空地址，防止绕过文档校验直接调用组件。 */
export const sanitizeRuntimeNavigationHref = (value: unknown): string => {
  const href = resolveRuntimeNavigationHref(value);
  return isSafeRuntimeNavigationHref(href) ? href : '';
};

/** 在渲染边界过滤 Link ping 地址列表，任一地址危险时禁用整组旁路请求。 */
export const sanitizeRuntimeNavigationPing = (value: unknown): string | undefined => (
  isSafeRuntimeNavigationPing(value) ? value : undefined
);
